import {
  type ExtendedLocationData,
  isDefined,
  type LocationId,
  type LocationTransferInput,
  type LocationTransferSuccess,
  messageFromError,
  toResponseError,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { useTantalimMutation } from '../mutation'
import { accountKey, customLocationsKey, eventKey, locationKey, locationTransferPreviewKey } from '../keys'
import { type GetHookResponse } from '../hooks'
import { conversationKey } from '../conversation/keys'
import { useLocationCache } from './cache'

type LocationTransferPair = {
  from: LocationId
  to: LocationId
}

/** Dry run of /location/transfer: what would move, without saving anything. */
export function useLocationTransferPreview({
  from,
  to,
}: LocationTransferPair): GetHookResponse<LocationTransferSuccess> {
  return useTantalimQuery<LocationTransferInput, LocationTransferSuccess, LocationTransferSuccess>({
    url: '/location/transfer',
    queryKey: locationTransferPreviewKey(from, to),
    input: { from, to, mode: 'dryRun' },
    convertResponse: (data) => data,
    options: { staleTime: 0 },
  })
}

type UseLocationTransferResponse = {
  transfer: (pair: LocationTransferPair) => Promise<LocationTransferSuccess>
  isTransferring: boolean
  error?: string
}

/**
 * Moves accounts, events and conversations between locations; a custom `from` location is deleted afterwards unless
 * other records still reference it.
 */
export function useLocationTransfer(): UseLocationTransferResponse {
  const mutation = useTantalimMutation<LocationTransferInput, LocationTransferSuccess>({ url: '/location/transfer' })
  const refreshAfterTransfer = useRefreshAfterTransfer()

  async function transfer(pair: LocationTransferPair): Promise<LocationTransferSuccess> {
    try {
      const output = await mutation.mutateAsync({ ...pair, mode: 'fullRun' })
      refreshAfterTransfer(pair, output)
      return output
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return { transfer, isTransferring: mutation.isPending, error: messageFromError(mutation.error) }
}

/**
 * Lists that can still show moved records on their old location. Their keys include filters, so they're matched by
 * prefix: accountsKey, accountsInfiniteKey, eventsKey, eventsInfiniteKey and conversationsKey.
 */
const MOVED_DATA_LIST_KEYS = new Set<unknown>([
  'accounts',
  'accounts-infinite',
  'calendar-events',
  'calendar-events-infinite',
  'conversations',
])

/** Refreshes everything a transfer can change: both locations, their map areas, the moved records and their lists. */
function useRefreshAfterTransfer(): (pair: LocationTransferPair, output: LocationTransferSuccess) => void {
  const queryClient = useQueryClient()
  const cache = useLocationCache()

  return ({ from, to }, output) => {
    // read the pins' coordinates before clearing their cached details
    const latlngs = [from, to]
      .map((locationId) => queryClient.getQueryData<ExtendedLocationData>(locationKey(locationId))?.latlng)
      .filter(isDefined)
    latlngs.forEach((latlng) => cache.invalidateSectorCache(latlng))
    cache.clearItem(from)
    cache.clearItem(to)

    output.accounts.forEach(({ accountId }) => queryClient.invalidateQueries({ queryKey: accountKey(accountId) }))
    output.events.forEach(({ eventId }) => queryClient.invalidateQueries({ queryKey: eventKey(eventId) }))
    output.conversations.forEach(({ convoId }) => queryClient.invalidateQueries({ queryKey: conversationKey(convoId) }))
    queryClient.invalidateQueries({ predicate: ({ queryKey }) => MOVED_DATA_LIST_KEYS.has(queryKey[0]) })
    queryClient.invalidateQueries({ queryKey: customLocationsKey })
  }
}
